/**
 * 移动端仪表盘页面
 * 支持日期筛选，可查看历史日期的数据概览
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Grid, Skeleton, Empty, Badge, ProgressBar } from 'antd-mobile';
import { adminDashboardApi } from '../../api/index';
import Toast from 'antd-mobile/es/components/toast';
import dayjs from 'dayjs';

export default function MobileDashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [selectedDate, setSelectedDate] = useState(dayjs());

  const fetchStats = useCallback(async (date) => {
    try {
      const dateStr = date ? date.format('YYYY-MM-DD') : null;
      const data = await adminDashboardApi.getStats(dateStr);
      setStats(data);
    } catch (err) {
      Toast.show('获取仪表盘数据失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats(selectedDate);
    // 每 30 秒刷新一次（仅当天）
    const timer = setInterval(() => {
      if (selectedDate && selectedDate.isSame(dayjs(), 'day')) {
        fetchStats(selectedDate);
      }
    }, 30000);
    return () => clearInterval(timer);
  }, [fetchStats, selectedDate]);

  const handleDateChange = (val) => {
    const date = dayjs(val);
    setSelectedDate(date);
    setLoading(true);
    fetchStats(date);
  };

  // 判断是否为今天
  const isToday = selectedDate && selectedDate.isSame(dayjs(), 'day');
  const dateLabel = isToday ? '今日' : selectedDate ? selectedDate.format('MM-DD') : '今日';

  const visitor = stats?.visitorStats;
  const todayOrders = stats?.todayOrders || 0;
  const todayVisitors = visitor?.todayVisitors || 0;
  const conversionRate = todayVisitors > 0 ? ((todayOrders / todayVisitors) * 100).toFixed(1) : 0;

  const statCards = [
    {
      title: `${dateLabel}订单`,
      value: stats?.todayOrders || 0,
      color: '#722ed1',
      icon: '📈',
    },
    {
      title: `${dateLabel}收入`,
      value: `$${(stats?.todayRevenue || 0).toFixed(2)}`,
      color: '#eb2f96',
      icon: '💰',
    },
  ];

  if (loading) {
    return (
      <div style={{ padding: 16 }}>
        <Skeleton.Title rows={2} style={{ marginBottom: 16 }} />
        <Skeleton.Title rows={4} />
      </div>
    );
  }

  return (
    <div style={{ padding: 16, paddingBottom: 60 }}>
      {/* 日期选择器 */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>数据概览</h2>
        <input
          type="date"
          max={dayjs().format('YYYY-MM-DD')}
          value={selectedDate ? selectedDate.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD')}
          onChange={(e) => {
            if (e.target.value) {
              handleDateChange(dayjs(e.target.value));
            }
          }}
          style={{
            fontSize: 14,
            color: '#1677ff',
            border: '1px solid #1677ff',
            borderRadius: 4,
            padding: '4px 8px',
            background: '#e6f4ff',
            width: 120,
          }}
        />
      </div>

      {/* 数据概览 */}
      <div style={{ marginBottom: 16 }}>
        <Grid columns={2} gap={12}>
          {statCards.map((card, index) => (
            <Grid.Item key={index}>
              <Card style={{ padding: 16, textAlign: 'center', background: `${card.color}08` }}>
                <div style={{ fontSize: 20, marginBottom: 4 }}>{card.icon}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: card.color }}>
                  {card.value}
                </div>
                <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
                  {card.title}
                </div>
              </Card>
            </Grid.Item>
          ))}
        </Grid>
      </div>

      {/* 访客概况 */}
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>{dateLabel}访客概况</h2>
        <Card style={{ padding: 16 }}>
          <Grid columns={2} gap={12}>
            {isToday && (
              <Grid.Item>
                <div style={{ textAlign: 'center' }}>
                  <Badge content={visitor?.onlineVisitors || 0} style={{ '--background': '#52c41a' }} />
                  <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>当前在线</div>
                </div>
              </Grid.Item>
            )}
            <Grid.Item>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 22, fontWeight: 600, color: '#1677ff' }}>{visitor?.todayVisitors || 0}</div>
                <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>{dateLabel}访客</div>
              </div>
            </Grid.Item>
            <Grid.Item>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 22, fontWeight: 600, color: '#faad14' }}>{visitor?.todayPageViews || 0}</div>
                <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>{dateLabel}浏览量</div>
              </div>
            </Grid.Item>
            <Grid.Item>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 22, fontWeight: 600, color: '#722ed1' }}>{conversionRate}%</div>
                <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>转化率</div>
              </div>
            </Grid.Item>
          </Grid>
        </Card>
      </div>

      {/* 近7天访客趋势 */}
      {visitor?.dailyVisitors && visitor.dailyVisitors.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>近7天访客趋势</h2>
          <Card style={{ padding: 16 }}>
            <MiniBarChart data={visitor.dailyVisitors} height={120} />
          </Card>
        </div>
      )}

      {/* 今日访客地区 */}
      {visitor?.countryStats && visitor.countryStats.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>{dateLabel}访客地区</h2>
          <Card style={{ padding: 12 }}>
            {visitor.countryStats.slice(0, 5).map((item, index) => {
              const maxVisitors = Math.max(...visitor.countryStats.map((c) => Number(c.visitors) || 0), 1);
              const percent = Math.round(((Number(item.visitors) || 0) / maxVisitors) * 100);
              return (
                <div key={index} style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{
                    width: 20, height: 20, borderRadius: 10, background: '#f0f0f0',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 11, color: '#999',
                  }}>
                    {index + 1}
                  </span>
                  <span style={{ flex: 1, fontSize: 14 }}>{item.country}</span>
                  <ProgressBar percent={percent} style={{ width: 60 }} />
                  <span style={{ fontSize: 14, fontWeight: 600, minWidth: 32, textAlign: 'right' }}>
                    {item.visitors}
                  </span>
                </div>
              );
            })}
          </Card>
        </div>
      )}

      {/* 今日访问页面统计 */}
      {visitor?.pageStats && visitor.pageStats.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>{dateLabel}访问页面</h2>
          <Card style={{ padding: 12 }}>
            {visitor.pageStats.slice(0, 10).map((item, index) => {
              const maxViews = Math.max(...visitor.pageStats.map((p) => Number(p.views) || 0), 1);
              const percent = Math.round(((Number(item.views) || 0) / maxViews) * 100);
              return (
                <div key={index} style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{
                    width: 20, height: 20, borderRadius: 10, background: '#f0f0f0',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 11, color: '#999', flexShrink: 0,
                  }}>
                    {index + 1}
                  </span>
                  <span style={{
                    flex: 1, fontSize: 12, color: '#333',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {item.page || '/'}
                  </span>
                  <ProgressBar percent={percent} style={{ width: 50 }} />
                  <span style={{ fontSize: 13, fontWeight: 600, minWidth: 28, textAlign: 'right', color: '#1677ff' }}>
                    {item.views}
                  </span>
                </div>
              );
            })}
          </Card>
        </div>
      )}

      {/* 快速入口 */}
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>快速入口</h2>
        <Grid columns={3} gap={12}>
          <Grid.Item>
            <Card
              style={{ padding: 16, textAlign: 'center' }}
              onClick={() => navigate('/admin/m/orders')}
            >
              <div style={{ fontSize: 24 }}>📦</div>
              <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>订单</div>
            </Card>
          </Grid.Item>
          <Grid.Item>
            <Card
              style={{ padding: 16, textAlign: 'center' }}
              onClick={() => navigate('/admin/m/products')}
            >
              <div style={{ fontSize: 24 }}>🛍️</div>
              <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>商品</div>
            </Card>
          </Grid.Item>
          <Grid.Item>
            <Card
              style={{ padding: 16, textAlign: 'center' }}
              onClick={() => navigate('/admin/m/inventory')}
            >
              <div style={{ fontSize: 24 }}>📊</div>
              <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>库存</div>
            </Card>
          </Grid.Item>
          <Grid.Item>
            <Card
              style={{ padding: 16, textAlign: 'center' }}
              onClick={() => navigate('/admin/m/customers')}
            >
              <div style={{ fontSize: 24 }}>👥</div>
              <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>客户</div>
            </Card>
          </Grid.Item>
          <Grid.Item>
            <Card
              style={{ padding: 16, textAlign: 'center' }}
              onClick={() => navigate('/admin/m/recent-carts')}
            >
              <div style={{ fontSize: 24 }}>🛒</div>
              <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>最近购物车</div>
            </Card>
          </Grid.Item>
          <Grid.Item>
            <Card
              style={{ padding: 16, textAlign: 'center' }}
              onClick={() => navigate('/admin/m/more')}
            >
              <div style={{ fontSize: 24 }}>⚙️</div>
              <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>更多</div>
            </Card>
          </Grid.Item>
        </Grid>
      </div>

      {/* 最近订单 */}
      <div>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>最近订单</h2>
        {stats?.recentOrders && stats.recentOrders.length > 0 ? (
          stats.recentOrders.map((order) => (
            <Card
              key={order.id}
              style={{ marginBottom: 12 }}
              onClick={() => navigate(`/admin/m/orders/${order.id}`)}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 14, fontFamily: 'monospace' }}>#{order.orderNo}</span>
                <StatusTag status={order.status} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 13, color: '#999' }}>
                  {order.customerEmail || order.email}
                </span>
                <span style={{ fontSize: 15, fontWeight: 600, color: '#52c41a' }}>
                  ${order.total?.toFixed(2) || '0.00'}
                </span>
              </div>
              <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                {order.createdAt ? new Date(order.createdAt).toLocaleString('zh-CN', { hour12: false }) : '-'}
              </div>
            </Card>
          ))
        ) : (
          <Empty description="暂无订单" />
        )}
      </div>
    </div>
  );
}

// 订单状态标签组件
function StatusTag({ status }) {
  const statusMap = {
    pending: { text: '待支付', color: '#faad14' },
    paid: { text: '已支付', color: '#52c41a' },
    shipped: { text: '已发货', color: '#1677ff' },
    completed: { text: '已完成', color: '#722ed1' },
    cancelled: { text: '已取消', color: '#ff4d4f' },
  };

  const config = statusMap[status] || { text: status, color: '#999' };

  return (
    <span
      style={{
        fontSize: 12,
        color: config.color,
        background: `${config.color}15`,
        padding: '2px 8px',
        borderRadius: 4,
      }}
    >
      {config.text}
    </span>
  );
}

// 简易柱状图组件
function MiniBarChart({ data, height = 100 }) {
  if (!data || data.length === 0) return null;
  const maxVal = Math.max(...data.map((d) => Number(d.visitors) || 0), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height }}>
      {data.map((d, i) => {
        const val = Number(d.visitors) || 0;
        const barH = Math.max((val / maxVal) * (height - 36), 2);
        return (
          <div key={i} style={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#1677ff', marginBottom: 2 }}>
              {val}
            </div>
            <div
              style={{
                height: barH,
                background: 'linear-gradient(180deg, #1677ff 0%, #69b1ff 100%)',
                borderRadius: '4px 4px 0 0',
                marginBottom: 4,
              }}
            />
            <div style={{ fontSize: 10, color: '#999' }}>
              {d.date?.slice(5) || ''}
            </div>
          </div>
        );
      })}
    </div>
  );
}
